"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import LoadingLink from "@/components/navigation/LoadingLink";
import Logo from "./Logo";
import { supabase } from "@/lib/supabaseClient";

export default function Header() {
  const pathname = usePathname();
  const [user, setUser] = useState<{ id: string; email?: string; user_metadata?: { full_name?: string; name?: string } } | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const init = async () => {
      const {
        data: { user: u },
      } = await supabase.auth.getUser();
      setUser(u ?? null);
      setAuthLoading(false);
    };
    void init();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, [mounted]);

  if (!mounted) {
    return (
      <header className="sticky top-0 z-20 border-b border-slate-800/80 bg-slate-950/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
          <Logo href="/" className="shrink-0" />
          <nav className="flex items-center gap-4 text-sm font-medium text-slate-200/80 sm:gap-6">
            <LoadingLink href="/" className="hover:text-slate-50">Home</LoadingLink>
            <span className="invisible h-0 w-0 overflow-hidden" aria-hidden="true">Loading</span>
          </nav>
        </div>
      </header>
    );
  }

  if (pathname?.startsWith("/dashboard") || pathname?.startsWith("/admin") || pathname === "/interview") {
    return null;
  }

  const displayName =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email?.split("@")[0] ||
    "User";
  const firstName = displayName.split(" ")[0] || displayName;

  const isHome = pathname === "/";

  if (isHome) {
    return (
      <header className="sticky top-0 z-20 border-b border-slate-800/80 bg-slate-950/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
          <Logo href="/" className="shrink-0" />
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-200/80 sm:flex">
            <a href="/how-it-works" className="hover:text-slate-50">
              How it works
            </a>
            <a href="/#what-you-get" className="hover:text-slate-50">
              What you get
            </a>
            <a href="/#pricing" className="hover:text-slate-50">
              Pricing
            </a>
            <a href="/#faq" className="hover:text-slate-50">
              FAQ
            </a>
          </nav>
          <div className="hidden items-center gap-2 sm:flex">
            {!authLoading &&
              (user ? (
                <>
                  <span className="text-xs font-medium text-slate-300">
                    Hello {firstName}
                  </span>
                  <LoadingLink
                    href="/dashboard"
                    className="rounded-full border border-slate-600 bg-slate-900/70 px-3.5 py-1.5 text-xs font-semibold text-slate-200/90 shadow-sm transition hover:border-indigo-400 hover:text-indigo-200"
                  >
                    Dashboard
                  </LoadingLink>
                </>
              ) : (
                <>
                  <LoadingLink
                    href="/login"
                    className="rounded-full border border-slate-600 bg-slate-900/70 px-3.5 py-1.5 text-xs font-semibold text-slate-200/90 shadow-sm transition hover:border-indigo-400 hover:text-indigo-200"
                  >
                    Sign in
                  </LoadingLink>
                  <LoadingLink
                    href="/signup"
                    className="rounded-full bg-amber-400 px-4 py-1.5 text-xs font-semibold text-slate-950 shadow-sm shadow-amber-500/40 transition hover:bg-amber-300"
                  >
                    Sign up
                  </LoadingLink>
                </>
              ))}
          </div>
        </div>
        <div className="border-t border-slate-800/80 bg-slate-950/95 px-4 pb-2 pt-2 sm:hidden">
          <div className="mx-auto flex max-w-6xl items-center justify-end gap-2">
            {!authLoading &&
              (user ? (
                <>
                  <span className="text-[11px] font-medium text-slate-300">
                    Hello {firstName}
                  </span>
                  <LoadingLink
                    href="/dashboard"
                    className="rounded-full border border-slate-600 bg-slate-900/70 px-3 py-1.5 text-[11px] font-semibold text-slate-200/90 shadow-sm transition hover:border-indigo-400 hover:text-indigo-200"
                  >
                    Dashboard
                  </LoadingLink>
                </>
              ) : (
                <>
                  <LoadingLink
                    href="/login"
                    className="rounded-full border border-slate-600 bg-slate-900/70 px-3 py-1.5 text-[11px] font-semibold text-slate-200/90 shadow-sm transition hover:border-indigo-400 hover:text-indigo-200"
                  >
                    Sign in
                  </LoadingLink>
                  <LoadingLink
                    href="/signup"
                    className="rounded-full bg-amber-400 px-3.5 py-1.5 text-[11px] font-semibold text-slate-950 shadow-sm shadow-amber-500/40 transition hover:bg-amber-300"
                  >
                    Sign up
                  </LoadingLink>
                </>
              ))}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-20 border-b border-slate-800/80 bg-slate-950/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
        <Logo href="/" className="shrink-0" />
        <nav className="flex items-center gap-4 text-sm font-medium text-slate-200/80 sm:gap-6">
          <LoadingLink href="/" className="hover:text-slate-50">
            Home
          </LoadingLink>
          <Link href="/how-it-works" className="hidden hover:text-slate-50 sm:inline">
            How it works
          </Link>
          {!authLoading &&
            (user ? (
              <>
                <LoadingLink href="/dashboard" className="hover:text-slate-50">
                  Dashboard
                </LoadingLink>
                <span className="hidden text-slate-400 sm:inline">Hello {firstName}</span>
                <button
                  type="button"
                  onClick={async () => {
                    await supabase.auth.signOut();
                    window.location.href = "/";
                  }}
                  className="rounded-full border border-slate-600 bg-slate-900/70 px-3.5 py-1.5 text-xs font-semibold text-slate-200/90 transition hover:border-slate-500 hover:bg-slate-800/80"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <LoadingLink href="/login" className="hover:text-slate-50">
                  Sign in
                </LoadingLink>
                <LoadingLink
                  href="/signup"
                  className="rounded-full bg-amber-400 px-3.5 py-1.5 text-xs font-semibold text-slate-950 shadow-sm transition hover:bg-amber-300 sm:px-4"
                >
                  Sign up
                </LoadingLink>
              </>
            ))}
        </nav>
      </div>
    </header>
  );
}
